/**
 * Template Editor Dialog
 *
 * Dialog for creating and editing checklist templates.
 */

import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCreateTemplate, useUpdateTemplate } from '../hooks/useInstallationJobs';
import { ChecklistItemsEditor } from './ChecklistItemsEditor';
import { SensorFlagsEditor } from './SensorFlagsEditor';
import { ToolItemsEditor } from './ToolItemsEditor';
import { MaterialItemsEditor } from './MaterialItemsEditor';
import { TermsEditor } from './TermsEditor';
import type {
  InstallationJobTemplate,
  ChecklistItem,
  TemplateToolItem,
  TemplateMaterialItem,
  TemplateTermsItem,
} from '../types';

type TemplateChecklistItem = Omit<
  ChecklistItem,
  'id' | 'value' | 'completedAt' | 'completedByName' | 'notes' | 'photoFileId' | 'signatureFileId'
>;

interface TemplateEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: InstallationJobTemplate | null;
  onClose: () => void;
}

export function TemplateEditorDialog({
  open,
  onOpenChange,
  template,
  onClose,
}: TemplateEditorDialogProps) {
  const isEditing = !!template;

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [checklistItems, setChecklistItems] = useState<TemplateChecklistItem[]>([]);
  const [toolItems, setToolItems] = useState<TemplateToolItem[]>([]);
  const [materialItems, setMaterialItems] = useState<TemplateMaterialItem[]>([]);
  const [termsAndConditions, setTermsAndConditions] = useState<TemplateTermsItem[]>([]);
  const [sensorFlags, setSensorFlags] = useState<string[]>([]);

  // Mutations
  const createMutation = useCreateTemplate();
  const updateMutation = useUpdateTemplate();

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  // Reset form when template changes
  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description || '');
      setIsActive(template.isActive);
      setChecklistItems(template.checklistItems);
      setToolItems(template.toolItems || []);
      setMaterialItems(template.materialItems || []);
      setTermsAndConditions(template.termsAndConditions || []);
      setSensorFlags(template.defaultSensorFlags);
    } else {
      setName('');
      setDescription('');
      setIsActive(true);
      setChecklistItems([]);
      setToolItems([]);
      setMaterialItems([]);
      setTermsAndConditions([]);
      setSensorFlags([]);
    }
  }, [template, open]);

  const handleSubmit = async () => {
    // Validation
    if (!name.trim()) {
      toast.error('Template name is required');
      return;
    }

    if (checklistItems.length === 0) {
      toast.error('At least one checklist item is required');
      return;
    }

    // Check for duplicate keys
    const keys = checklistItems.map((item) => item.key);
    const duplicateKeys = keys.filter((key, index) => keys.indexOf(key) !== index);
    if (duplicateKeys.length > 0) {
      toast.error(`Duplicate item keys: ${duplicateKeys.join(', ')}`);
      return;
    }

    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        isActive,
        checklistItems,
        toolItems,
        materialItems,
        termsAndConditions,
        defaultSensorFlags: sensorFlags,
      };

      if (isEditing) {
        await updateMutation.mutateAsync({
          id: template.id,
          input: payload,
        });
        toast.success('Template updated successfully');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Template created successfully');
      }

      onClose();
    } catch {
      toast.error(isEditing ? 'Failed to update template' : 'Failed to create template');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Template' : 'Create Template'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the checklist template details and items.'
              : 'Create a new checklist template for installation jobs.'}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="details" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="checklist">
              Checklist ({checklistItems.length})
            </TabsTrigger>
            <TabsTrigger value="tools">
              Tools ({toolItems.length})
            </TabsTrigger>
            <TabsTrigger value="materials">
              Materials ({materialItems.length})
            </TabsTrigger>
            <TabsTrigger value="terms">
              Terms ({termsAndConditions.length})
            </TabsTrigger>
            <TabsTrigger value="flags">Flags ({sensorFlags.length})</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto mt-4">
            <TabsContent value="details" className="mt-0 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Template Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Standard Sensor Installation"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe when this template should be used..."
                  rows={3}
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label htmlFor="active">Active</Label>
                  <p className="text-sm text-muted-foreground">
                    Inactive templates won't appear when creating new jobs
                  </p>
                </div>
                <Switch id="active" checked={isActive} onCheckedChange={setIsActive} />
              </div>
            </TabsContent>

            <TabsContent value="checklist" className="mt-0">
              <ChecklistItemsEditor items={checklistItems} onChange={setChecklistItems} />
            </TabsContent>

            <TabsContent value="tools" className="mt-0">
              <ToolItemsEditor items={toolItems} onChange={setToolItems} />
            </TabsContent>

            <TabsContent value="materials" className="mt-0">
              <MaterialItemsEditor items={materialItems} onChange={setMaterialItems} />
            </TabsContent>

            <TabsContent value="terms" className="mt-0">
              <TermsEditor items={termsAndConditions} onChange={setTermsAndConditions} />
            </TabsContent>

            <TabsContent value="flags" className="mt-0">
              <SensorFlagsEditor flags={sensorFlags} onChange={setSensorFlags} />
            </TabsContent>
          </div>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEditing ? 'Save Changes' : 'Create Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
